<template>
  <div class="new-product-configurator-editor-accessory">
    <div
      v-for="(item, index) in items"
      :key="`${item.productId || item.value}_${index}`"
      :class="['accessory-item', { active: item.active }]"
    >
      <a
        v-if="item.url"
        class="open-link"
        :href="item.url"
        target="_blank"
        rel="noopener noreferrer"
        :title="translations.product_open_link || ''"
        @click.stop
      >
        <lazy-icon icon="b-box-arrow-up-right" />
      </a>

      <button class="accessory-select" type="button" @click="onAccessorySelect(item, $event)">
        <img v-if="item.imageUrl" :src="item.imageUrl" :alt="displayTitle(item)" class="accessory-image" />
        <div v-else class="accessory-image is-fallback">
          <lazy-icon icon="images" />
        </div>
        <div class="accessory-content">
          <div class="accessory-title1">{{ displayTitle(item) }}</div>
          <div class="accessory-title2">{{ displaySubtitle(item) }}</div>
          <c-product-delivery-state
            :state-id="item.deliveryStateId"
            :stock-amount="item.stockAmount || 0"
            :restock-time="item.restockTime || ''"
          />
          <div class="accessory-price" v-if="item.price != null">{{ currency(item) }} {{ item.price }}</div>
        </div>
        <lazy-icon v-if="item.active" icon="b-check-circle-fill" class="check-icon" />
      </button>

      <div class="accessory-quantity" v-if="item.active">
        <accessory-quantity-selector
          :value="item.quantity || 1"
          :quantityinfo="quantityInfo"
          :translations="translations"
          @input="(quantity) => $emit('product-configurator:accessory-quantity', { item, quantity })"
        />
      </div>

      <div v-if="item.active && item.requiredProducts && item.requiredProducts.length" class="required-products">
        <div class="required-title">
          <lazy-icon icon="b-exclamation-triangle-fill" class="mr-1" />
          {{ translations.requiredproducts_title || 'Zwingend notwendige Produkte' }}
        </div>
        <div v-if="!item.selectedRequiredProductId" class="required-warning">
          {{ translations.requiredproducts_selectone || 'Bitte genau ein Produkt auswählen' }}
        </div>
        <div
          v-for="(requiredProduct, requiredIndex) in item.requiredProducts"
          :key="`${requiredProduct.productId}_${requiredIndex}`"
          :class="['required-product-item', { active: item.selectedRequiredProductId === requiredProduct.productId }]"
          @click="$emit('product-configurator:required-product-select', { item, requiredProduct })"
        >
          <a
            v-if="requiredProduct.url"
            class="open-link required-open-link"
            :href="requiredProduct.url"
            target="_blank"
            rel="noopener noreferrer"
            :title="translations.product_open_link || ''"
            @click.stop
          >
            <lazy-icon icon="b-box-arrow-up-right" />
          </a>

          <img v-if="requiredProduct.imageUrl" :src="requiredProduct.imageUrl" :alt="displayRequiredTitle(requiredProduct)" class="required-image" />
          <div v-else class="required-image is-fallback"><lazy-icon icon="images" /></div>

          <div class="required-content">
            <div class="required-title1">{{ displayRequiredTitle(requiredProduct) }}</div>
            <div class="required-title2">{{ displayRequiredSubtitle(requiredProduct) }}</div>
            <c-product-delivery-state
              :state-id="requiredProduct.deliveryStateId"
              :stock-amount="requiredProduct.stockAmount || 0"
              :restock-time="requiredProduct.restockTime || ''"
            />
            <div class="required-price" v-if="requiredProduct.price != null">{{ requiredProduct.currency || 'CHF' }} {{ requiredProduct.price }}</div>
          </div>

          <div class="required-quantity">{{ item.quantity || 1 }}x</div>
          <lazy-icon v-if="item.selectedRequiredProductId === requiredProduct.productId" icon="b-check-circle-fill" class="required-check" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AccessoryQuantitySelector from '@/components/private/AccessoryQuantitySelector'

export default {
  name: 'NewProductConfiguratorEditorAccessory',
  components: {
    AccessoryQuantitySelector
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      quantityInfo: {
        min: 1,
        max: 1000,
        step: 1,
        initialStep: 1
      }
    }
  },
  methods: {
    onAccessorySelect(item, event) {
      const hasRequiredProducts = Array.isArray(item?.requiredProducts) && item.requiredProducts.length > 0
      const shouldScrollIntoView = !item.active && hasRequiredProducts

      this.$emit('product-configurator:accessory-select', item)

      if (!shouldScrollIntoView) {
        return
      }

      this.$nextTick(() => {
        this.scrollItemToTop(event?.currentTarget)
      })
    },
    scrollItemToTop(sourceElement) {
      const itemElement = sourceElement?.closest('.accessory-item')
      if (!itemElement) {
        return
      }

      const scrollParent = this.findScrollableParent(itemElement)
      if (!scrollParent) {
        return
      }

      const parentRect = scrollParent.getBoundingClientRect()
      const itemRect = itemElement.getBoundingClientRect()
      const targetTop = scrollParent.scrollTop + (itemRect.top - parentRect.top)

      scrollParent.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth'
      })
    },
    findScrollableParent(element) {
      let currentElement = element?.parentElement || null

      while (currentElement) {
        const style = window.getComputedStyle(currentElement)
        const overflowY = style.overflowY
        const isScrollable = ['auto', 'scroll', 'overlay'].includes(overflowY) && currentElement.scrollHeight > currentElement.clientHeight

        if (isScrollable) {
          return currentElement
        }

        currentElement = currentElement.parentElement
      }

      return null
    },
    displayTitle(item) {
      return this.localizedValue(item?.title1) || this.localizedValue(item?.name) || ''
    },
    displaySubtitle(item) {
      return this.localizedValue(item?.title2) || ''
    },
    currency(item) {
      return item?.currency || 'CHF'
    },
    localizedValue(value) {
      if (value == null) {
        return ''
      }

      if (typeof value === 'string') {
        return value
      }

      const locale = this.$i18n.locale
      return value[locale] || value[locale.split('-')[0]] || Object.values(value)[0] || ''
    },
    displayRequiredTitle(requiredProduct) {
      return this.localizedValue(requiredProduct?.title1) || this.localizedValue(requiredProduct?.name) || ''
    },
    displayRequiredSubtitle(requiredProduct) {
      return this.localizedValue(requiredProduct?.title2) || ''
    }
  }
}
</script>

<style lang="scss">
.new-product-configurator-editor-accessory {
  display: grid;
  gap: $spacer * 0.5;

  .accessory-item {
    position: relative;
    border: map-get($borders, 'base');
    border-radius: $border-radius-sm;

    &.active {
      border-color: $primary;
    }
  }

  .accessory-select {
    display: grid;
    grid-template-columns: 72px 1fr;
    gap: $spacer * 0.5;
    width: 100%;
    padding: $spacer * 0.5;
    border: 0 none;
    background: transparent;
    text-align: left;
  }

  .accessory-image {
    width: 72px;
    height: 54px;
    object-fit: contain;

    &.is-fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $gray-500;
      background-color: transparent;
    }
  }

  .accessory-title1 {
    font-weight: $font-weight-medium;
  }

  .accessory-title2 {
    font-size: $font-size-sm;
    color: $gray-600;
  }

  .accessory-price {
    margin-top: $spacer * 0.25;
    font-weight: $font-weight-medium;
  }

  .accessory-quantity {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 $spacer * 0.5 $spacer * 0.5;

    .accessory-quantity-selector {
      margin-right: 0.2rem;
    }
  }

  .required-products {
    margin: 0 $spacer * 0.5 $spacer * 0.5;
    padding: $spacer * 0.65;
    border: 1px solid rgba($warning, 0.5);
    background-color: rgba($warning, 0.12);
    border-radius: $border-radius-sm;
  }

  .required-title {
    display: flex;
    align-items: center;
    margin-bottom: $spacer * 0.5;
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
  }

  .required-product-item {
    position: relative;
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: $spacer * 0.4;
    align-items: center;
    margin-top: $spacer * 0.35;
    padding: $spacer * 0.45;
    border-top: 1px solid rgba($warning, 0.35);
    background-color: rgba($white, 0.35);
    border-radius: $border-radius-sm;

    &:first-of-type {
      border-top: 0 none;
      margin-top: 0;
    }

    cursor: pointer;

    &.active {
      background-color: rgba($warning, 0.24);
      border: 1px solid rgba($warning, 0.55);
      box-shadow: inset 0 0 0 1px rgba($white, 0.35);
    }
  }

  .required-image {
    width: 48px;
    height: 36px;
    object-fit: contain;

    &.is-fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      color: $gray-500;
      background-color: transparent;
    }
  }

  .required-title1 {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    margin-bottom: 0.1rem;
  }

  .required-title2 {
    font-size: 0.75rem;
    color: $gray-700;
    margin-bottom: 0.2rem;
  }

  .required-price {
    font-size: 0.75rem;
    font-weight: $font-weight-medium;
  }

  .required-quantity {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: $gray-800;
    margin-left: $spacer * 0.25;
  }

  .required-check {
    position: absolute;
    top: 0.5rem;
    right: 0.45rem;
    color: $gray-900;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    font-size: 0.92rem;
  }

  .required-warning {
    margin-bottom: $spacer * 0.35;
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: darken($warning, 25%);
    padding: 0.2rem 0.45rem;
    border-radius: $border-radius-sm;
    background-color: rgba($warning, 0.2);
  }

  .check-icon {
    position: absolute;
    top: 0.5rem;
    right: 0.45rem;
    color: $gray-900;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    font-size: 0.92rem;
  }

  .open-link {
    position: absolute;
    top: 0.5rem;
    right: 1.8rem;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.9rem;
    height: 0.9rem;
    font-size: 0.78rem;
    line-height: 1;
    color: $gray-700;
    text-decoration: none;

    &:hover {
      color: $gray-900;
    }
  }

  .required-open-link {
    top: 0.5rem;
    right: 1.8rem;
  }

  .accessory-content,
  .required-content {
    padding-right: 2.4rem;
  }
}
</style>
